import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { agentsAPI } from '../services/api';

/**
 * Enroll a domain controller, or reissue a token for one.
 *
 * The important property of this screen: the enrollment token is returned in
 * plaintext EXACTLY ONCE. The hub stores only its hash, so there is no "show it
 * again" -- which is why the success state is not a toast but a full panel with
 * the commands already assembled. An operator who closes this dialog without
 * copying has to reissue, and reissuing is deliberately cheap for that reason.
 */

const TTL_OPTIONS = [
  { value: 900, label: '15 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 86400, label: '24 hours' },
];

function CopyBlock({ text, label }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked outside a secure context. The text is selectable
      // on screen, so this is a degraded path, not a broken one.
      setCopied(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          pr: 6,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {text}
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton
            size="small"
            onClick={copy}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              // transform/opacity only -- these run on the compositor and stay
              // smooth; animating size or position would cause layout jank.
              transition: 'transform 140ms cubic-bezier(.2,.8,.2,1), opacity 140ms',
              '&:hover': { transform: 'scale(1.12)' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function AgentEnrollDialog({ open, onClose, onEnrolled, existingAgent = null, hubUrl }) {
  const reissue = Boolean(existingAgent);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [ttl, setTtl] = useState(3600);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState(null);

  const reset = () => {
    setName('');
    setDescription('');
    setIsDefault(false);
    setTtl(3600);
    setError('');
    setIssued(null);
    setBusy(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = reissue
        ? await agentsAPI.reissueEnrollment(existingAgent.name, { ttl_seconds: ttl })
        : await agentsAPI.create({
            name: name.trim(),
            description: description.trim(),
            is_default: isDefault,
            ttl_seconds: ttl,
          });
      setIssued({
        token: res.data.enroll_token,
        expiresIn: res.data.expires_in,
        name: reissue ? existingAgent.name : name.trim(),
      });
      onEnrolled?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Enrollment failed');
    } finally {
      setBusy(false);
    }
  };

  // When the deployment has not declared its agent-facing address there is
  // nothing honest to print here, so say that instead of guessing one.
  const hub = hubUrl || '<AGENT_HUB_URL is not set on this deployment>';

  const install = issued
    ? [
        '# On the domain controller, as root:',
        `sudo install -o root -g root -m 0755 samba-agent /usr/local/bin/samba-agent`,
        '',
        '# Enroll once. The token is single-use and is consumed here; the agent',
        '# then stores its own long-lived token at /etc/samba-agent/agent.token.',
        `sudo -u samba-agent env \\`,
        `  SAMBA_AGENT_HUB=${hub} \\`,
        `  SAMBA_AGENT_ENROLL_TOKEN=${issued.token} \\`,
        `  SAMBA_AGENT_TOKEN_FILE=/etc/samba-agent/agent.token \\`,
        `  /usr/local/bin/samba-agent run --wait-secs 25`,
        '',
        '# Then run it as a service (survives reboot):',
        'sudo systemctl enable --now samba-agent',
      ].join('\n')
    : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {issued
          ? `Enrollment token for "${issued.name}"`
          : reissue
          ? `Reissue enrollment token for "${existingAgent.name}"`
          : 'Enroll a domain controller'}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {issued ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Copy this now — it is shown once</AlertTitle>
              The hub stores only a hash of this token, so it cannot be displayed again.
              It is single-use and expires in{' '}
              {issued.expiresIn >= 3600
                ? `${Math.round(issued.expiresIn / 3600)} hour(s)`
                : `${Math.round(issued.expiresIn / 60)} minutes`}
              . If you lose it, reissue — that is cheaper than a token that lives forever.
            </Alert>

            <CopyBlock label="Enrollment token" text={issued.token} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Install on the domain controller
            </Typography>
            <CopyBlock text={install} />

            {!hubUrl && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>This deployment has not declared its agent address</AlertTitle>
                Set <code>AGENT_HUB_URL</code> on the backend to the TLS endpoint agents should
                dial (the listener that serves <code>/api/agent/</code>), then substitute it in
                the command above. It is deliberately not the address of this web UI.
              </Alert>
            )}

            <Alert severity="info">
              The agent dials <strong>out</strong> to the hub and opens no inbound port on the
              domain controller. It executes only its compiled-in allowlist of operations — never
              a shell — and refuses destructive, schema and service operations that arrive
              without an approver.
            </Alert>
          </>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!reissue && (
              <>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  autoFocus
                  required
                  helperText="Short stable identifier for this DC, e.g. 'ldaps'. Used in job records."
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  helperText="Optional. Which host and realm this agent manages."
                />
                <FormControlLabel
                  control={
                    <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                  }
                  label="Use as the default agent"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, ml: 4 }}>
                  Requests that do not name an agent are sent here.
                </Typography>
              </>
            )}

            {reissue && (
              <Alert severity="info">
                This replaces any outstanding enrollment token for{' '}
                <strong>{existingAgent.name}</strong>. Use it when reinstalling the agent, or when
                the previous token expired before it was used.
              </Alert>
            )}

            <TextField
              select
              label="Token lifetime"
              value={ttl}
              onChange={(e) => setTtl(Number(e.target.value))}
              helperText="Short by default. An enrollment token sitting in someone's notes is a standing grant of this agent's identity."
            >
              {TTL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{issued ? 'Done' : 'Cancel'}</Button>
        {!issued && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || (!reissue && !name.trim())}
          >
            {busy ? 'Working…' : reissue ? 'Reissue token' : 'Create and issue token'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AgentEnrollDialog;
